import React, { useEffect, useRef } from 'react';
import cv from './cv.json';
import './CvPage.css';

// Команда, которая «печатается» перед каждым разделом
const COMMANDS = {
  summary: 'cat summary.txt',
  education: 'cat education.log',
  experience: 'cat experience.log',
  achievements: 'ls -l achievements/',
  projects: 'ls projects/',
  technical: 'cat skills.yaml',
};

const formatDate = iso =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const isoDay = iso => new Date(iso).toISOString().slice(0, 10);

// Блок «команда + вывод»; анимация запускается, когда блок попадает в экран
function Block({ command, children, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) {
      el && el.classList.add('is-visible');
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className={`cv-block ${className}`} style={{ '--len': command.length }}>
      <p className="cv-prompt">
        <span className="cv-prompt-sign" aria-hidden="true">$</span>{' '}
        <span className="cv-cmd">{command}</span>
      </p>
      <div className="cv-output">{children}</div>
    </section>
  );
}

function Entries({ entries }) {
  return (
    <ul className="cv-entries">
      {entries.map((e, i) => (
        <li key={i} className="cv-entry">
          <span className="cv-date">{e.date}</span>
          <div>
            <h3 className="cv-entry-title">
              {e.url ? (
                <a href={e.url} target="_blank" rel="noopener noreferrer">
                  {e.title} <span aria-hidden="true">↗</span>
                </a>
              ) : (
                e.title
              )}
            </h3>
            {e.subtitle && <p className="cv-entry-sub">{e.subtitle}</p>}
            {e.place && <p className="cv-entry-place">@ {e.place}</p>}
            {e.items.length > 0 && (
              <ul className="cv-items">
                {e.items.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Skills({ skills }) {
  return (
    <dl className="cv-skills">
      {skills.map(s => (
        <div key={s.label} className="cv-skill">
          <dt>{s.label.toLowerCase().replace(/\s*\/\s*/g, '_').replace(/\s+/g, '_')}:</dt>
          <dd>
            [
            {s.values.map((v, i) => (
              <span key={v}>
                {v}
                {i < s.values.length - 1 && ', '}
              </span>
            ))}
            ]
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function CvPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${cv.name} — CV`;
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="cv-page">
      <header className="cv-topbar">
        <a href="/" className="cv-home">
          ← cd ~
        </a>
        <span className="cv-updated">
          last updated <time dateTime={cv.lastUpdated}>{formatDate(cv.lastUpdated)}</time>
        </span>
      </header>

      <main className="cv-main">
        <Block command="whoami" className="cv-hero">
          <div className="cv-hero-inner">
            <div>
              <h1 className="cv-name">{cv.name.toLowerCase()}</h1>
              <p className="cv-title">
                {cv.title} <span className="cv-dim">· {cv.location}</span>
              </p>
              <ul className="cv-links">
                {cv.links.map(l => (
                  <li key={l.url}>
                    <a
                      href={l.url}
                      {...(l.url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            {cv.photo && <img className="cv-photo" src={cv.photo} alt={cv.name} width="120" height="120" />}
          </div>
        </Block>

        {cv.sections.map(section => (
          <Block key={section.id} command={COMMANDS[section.id] || `cat ${section.id}`}>
            <h2 className="cv-section-title"># {section.title}</h2>
            {section.type === 'text' && <p className="cv-text">{section.text}</p>}
            {section.type === 'entries' && <Entries entries={section.entries} />}
            {section.type === 'skills' && <Skills skills={section.skills} />}
          </Block>
        ))}

        {cv.pdf && (
          <Block command="wget cv.pdf">
            <a className="cv-download" href={cv.pdf} download>
              [ ↓ {cv.pdf.split('/').pop()} ]
            </a>
            <p className="cv-dim cv-comment"># last modified {isoDay(cv.lastUpdated)}</p>
          </Block>
        )}

        <p className="cv-prompt cv-idle" aria-hidden="true">
          <span className="cv-prompt-sign">$</span> <span className="cv-cursor" />
        </p>
      </main>
    </div>
  );
}
